import React, { useState } from "react";
import { MapPin, Mail, Phone, Instagram, AlertCircle, CheckCircle } from 'lucide-react'; // Added AlertCircle, CheckCircle
import axios from 'axios'; // Import axios
import Footer from '../components/Footer';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false); // New state for loading
  const [successMessage, setSuccessMessage] = useState(''); // New state for success message
  const [errorMessage, setErrorMessage] = useState(''); // New state for error message

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => { // Made the function async
    e.preventDefault(); // Prevent default form submission
    setIsLoading(true); // Set loading to true
    setSuccessMessage(''); // Clear previous messages
    setErrorMessage('');

    try {
      const response = await axios.post('https://api.cafpraticheonline.it/api/contact', formData);

      if (response.status === 201) { // Backend returns 201 for successful creation
        setSuccessMessage(response.data.message || 'Messaggio inviato con successo!');
        setFormData({ // Clear the form fields
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Errore nell invio del modulo di contatto:', error.response ? error.response.data : error.message);
      setErrorMessage(error.response?.data?.message || 'Invio del messaggio non riuscito. Per favore riprova.');
    } finally {
      setIsLoading(false); // Set loading to false regardless of success or error
    }
  };

  // Gradients
  const yellowGradient = 'linear-gradient(135deg, #F2C231 0%, #C69A32 100%)';
  const blueGradient = 'linear-gradient(135deg, #1D3A50 0%, #182F40 100%)';

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="text-center py-12 px-4 bg-white pb-2">
        <h1 className="text-5xl md:text-6xl font-bold mb-4 text-slate-800">
          CONTATTACI
        </h1>
        <div
          className="w-64 h-1 mx-auto mb-8 rounded"
          style={{ background: yellowGradient }}
        />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Side - Contact Form */}
          <div
            className="rounded-2xl p-8 shadow-2xl"
            style={{ background: blueGradient }}
          >
            <h2 className="text-3xl font-bold mb-8 text-center text-white">METTITI IN CONTATTO</h2>

            <form onSubmit={handleSubmit} className="space-y-6"> {/* Added form tag and onSubmit */}
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Il tuo nome"
                className="w-full px-4 py-4 bg-gray-200 rounded-lg text-slate-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition-all duration-300"
                required // Added required attribute
              />

              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="La tua email"
                className="w-full px-4 py-4 bg-gray-200 rounded-lg text-slate-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition-all duration-300"
                required // Added required attribute
              />

              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Il tuo numero di telefono (Opzionale)" // Clarified optional
                className="w-full px-4 py-4 bg-gray-200 rounded-lg text-slate-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition-all duration-300"
              />

              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Il tuo messaggio"
                rows={6}
                className="w-full px-4 py-4 bg-gray-200 rounded-lg text-slate-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition-all duration-300 resize-vertical"
                required // Added required attribute
              />

              {successMessage && (
                <div className="flex items-center text-green-400 text-sm mt-2">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {successMessage}
                </div>
              )}

              {errorMessage && (
                <div className="flex items-center text-red-400 text-sm mt-2">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  {errorMessage}
                </div>
              )}

              <button
                type="submit" // Changed to type="submit"
                disabled={isLoading} // Disable button when loading
                className="w-full py-4 px-6 text-slate-800 font-bold rounded-lg transition-all duration-300 hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: yellowGradient }}
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-800 mx-auto"></div>
                ) : (
                  'INVIA MESSAGGIO'
                )}
              </button>
            </form>
          </div>

          {/* Right Side - Contact Information */}
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <h2 className="text-3xl font-bold mb-6 text-center text-slate-800">INFORMAZIONI DI CONTATTO</h2>

            <div className="mb-8">
              <p className="text-slate-600 text-center leading-relaxed">
                Siamo felici di sentirti! Non esitare a contattarci.
                Il nostro team è qui per assisterti con qualsiasi domanda tu possa avere.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: yellowGradient }}
                >
                  <MapPin className="text-slate-800" size={20} />
                </div>
                <div>
                  <p className="text-slate-800 text-lg font-medium">
                     <a
                      href="https://www.google.com/maps/place/Via+Andrea+Costa,+15,+42017+Novellara+RE,+Italy/@44.8459327,10.7249985,617m/data=!3m2!1e3!4b1!4m6!3m5!1s0x478020937fe4ab2f:0xf41eca1a7f48ce79!8m2!3d44.8459327!4d10.7275734!16s%2Fg%2F11q2nd3f3k?entry=ttu&g_ep=EgoyMDI1MDkyOC4wIKXMDSoASAFQAw%3D%3D"
                      className="hover:underline text-inherit"
                    >
                       Via Andrea Costa 15 Novellara, Re 42017
                    </a>
                   
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: yellowGradient }}
                >
                  <Phone className="text-slate-800" size={20} />
                </div>
                <div>
                  <p className="text-slate-800 text-lg font-medium">
                    <a
                      href="tel:+393428439658"
                      className="hover:underline text-inherit"
                    >
                      +39 342 843 9658
                    </a>

                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: yellowGradient }}
                >
                  <Mail className="text-slate-800" size={20} />
                </div>
                <div>
                  <p className="text-slate-800 text-lg font-medium">
                    <a
                      href="mailto: cafpraticheonline@gmail.com"
                      className="hover:underline text-inherit"
                    >
                       cafpraticheonline@gmail.com
                    </a>

                  </p>
                </div>
              </div>
            </div>

            {/* Follow Us Icons */}
            <div className="mt-12 flex justify-center">
              <a
                href="#"
                className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                style={{ background: blueGradient }}
              >
                <Instagram className="#C69A32" size={28} style={{ color: '#C69A32' }} />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto my-6">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2688.836766537697!2d10.724998476069429!3d44.84593267107057!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478020937fe4ab2f%3A0xf41eca1a7f48ce79!2sVia%20Andrea%20Costa%2C%2015%2C%2042017%20Novellara%20RE%2C%20Italy!5e1!3m2!1sen!2s!4v1759266340770!5m2!1sen!2s"
          width="100%"
          height="300"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>

    </div>
  );
}